import type { AuditHandoffAction } from '@compass/client/types/audit';
import { Button } from '@components/ui/button';
import { Textarea } from '@components/ui/textarea';
import { SimpleTooltipV2 } from '@components/ui/tooltip';
import { ChevronDown, ChevronUp } from 'lucide-react';
import React, { useMemo, useState } from 'react';

import type { FieldGroup } from '@/types/formSchema';
import { handoffFieldLabel, isCaseLevelHandoff } from '@/utils/handoffField';

import { HandoffFieldEditor } from './HandoffFieldEditor';

interface HandoffActionRowProps {
  action: AuditHandoffAction;
  /** Stages this action for the human caller with `note` as the reason. */
  onEscalate: (note: string) => void;
  /** When false the row is view-only: no Escalate/Resolve affordances. */
  canEdit: boolean;
  outputGroups: FieldGroup[];
  outputData: Record<string, unknown> | undefined;
  onCommitField: (path: string, value: unknown) => Promise<void> | void;
  caseRecordId?: string;
}

/**
 * One handoff action from the AI auditor, with the two dispositions the human auditor
 * can take on it (BI-1668, BI-1681):
 *
 *  - **Escalate** — hand it to the human caller: a note box opens right below the field,
 *    seeded with the auditor's note. Save MOVES the action out of the handoff list and
 *    into the "Escalate to human caller" section under it (written on route-to-human-call,
 *    see DefaultCaseDetailsPage), so each item sits in exactly one of the two places and
 *    the list shrinks to what is still undecided. Removing it there brings the row back
 *    (see HumanAuditTab) — which is why this row carries no "escalated" state of its own.
 *    Editing the note before saving is what makes a **case-level** action — rendered at
 *    the top of the list with no field name — usable as a free-form custom handoff.
 *  - **Resolve** — fix it here: reveals the flagged OUTPUT field inline
 *    ({@link HandoffFieldEditor}) so the auditor can correct the value without hunting
 *    for it in the Output sidebar. Toggles closed on a second click. A case-level action
 *    points at no field, so it offers no Resolve.
 *
 * The two are MUTUALLY EXCLUSIVE per action — an item is either being handed off or
 * being fixed here, never both, so the auditor can't quietly edit a field they have
 * also asked the caller to chase. Each choice is reversible and unlocks the other:
 * remove the escalation (from the section below) to bring the row back with Resolve
 * available again, or click Resolve again to close the editor and re-enable Escalate.
 *
 * HIPAA: the note and field values may be PHI — display/persist only, never logged.
 */
const HandoffActionRowComponent: React.FC<HandoffActionRowProps> = ({
  action,
  onEscalate,
  canEdit,
  outputGroups,
  outputData,
  onCommitField,
  caseRecordId,
}) => {
  const [isResolving, setIsResolving] = useState(false);
  // The note box is open while composing the escalation; `draft` holds the in-flight text
  // until Save (which hands the action off and unmounts this row).
  const [draft, setDraft] = useState<string | null>(null);
  const isComposing = draft !== null;
  // While the auditor is writing the escalation note, the AI's note collapses to ONE
  // line so the field and the box being typed in are never pushed apart by a long note;
  // a chevron expands it on demand.
  const [isNoteExpanded, setIsNoteExpanded] = useState(false);
  const isNoteCollapsed = isComposing && !isNoteExpanded;

  // A case-level action names no field: it renders label-less and has nothing to resolve.
  const isCaseLevel = isCaseLevelHandoff(action);
  // The auditor writes a raw short field name; show the schema breadcrumb instead.
  const displayLabel = useMemo(
    () => handoffFieldLabel(outputGroups, action.field),
    [outputGroups, action.field],
  );
  const fieldLabel = isCaseLevel ? 'this case' : (action.field ?? 'this action');

  const openComposer = () => {
    setDraft(action.note ?? '');
    setIsNoteExpanded(false);
  };
  const closeComposer = () => setDraft(null);
  const saveComposer = () => {
    onEscalate((draft ?? '').trim());
    closeComposer();
  };

  // Escalate and Resolve lock each other out, so each disabled tooltip has to say how to
  // get unstuck — a bare label on a dead button reads as a bug.
  const escalateTooltip = isResolving
    ? 'Resolving here — close the field editor to escalate instead'
    : 'Escalate to human';
  const resolveTooltip = isResolving ? 'Hide the field' : 'Resolve — edit the field here';

  return (
    <li className="flex flex-col gap-1.5 border-b border-muted/60 pb-2.5 last:border-b-0 last:pb-0">
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1 flex flex-col gap-0.5">
          {displayLabel && (
            <span className="break-words text-xs font-medium text-foreground">{displayLabel}</span>
          )}
          {action.note &&
            (isComposing ? (
              // A single ellipsised line, with a chevron to read the whole thing without
              // closing the note box.
              <div className="flex items-start gap-1">
                <span
                  className={`min-w-0 flex-1 text-sm text-muted-foreground ${
                    isNoteCollapsed ? 'truncate' : 'whitespace-pre-wrap break-words'
                  }`}
                  title={isNoteCollapsed ? action.note : undefined}
                >
                  {action.note}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-5 w-5 shrink-0 p-0 text-muted-foreground"
                  aria-expanded={!isNoteCollapsed}
                  aria-label={isNoteCollapsed ? 'Show the full note' : 'Collapse the note'}
                  onClick={() => setIsNoteExpanded(v => !v)}
                >
                  {isNoteCollapsed ? (
                    <ChevronDown className="h-3.5 w-3.5" />
                  ) : (
                    <ChevronUp className="h-3.5 w-3.5" />
                  )}
                </Button>
              </div>
            ) : (
              <span className="whitespace-pre-wrap break-words text-sm text-muted-foreground">
                {action.note}
              </span>
            ))}
          {/* Resolve: the flagged output field, editable in place. Nested fields render
              with their parent chain so the leaf label reads in context; a case-level or
              unresolvable field renders nothing (see HandoffFieldEditor). */}
          {isResolving && (
            <HandoffFieldEditor
              fieldName={action.field}
              outputGroups={outputGroups}
              outputData={outputData}
              canEdit={canEdit}
              onCommit={onCommitField}
              caseRecordId={caseRecordId}
            />
          )}
        </div>
        {canEdit && (
          // Side by side, each with a min width so the two read as one pair of controls
          // and neither reflows the row, with real horizontal padding inside its border.
          <div className="flex shrink-0 items-start gap-1.5">
            <SimpleTooltipV2 content={escalateTooltip} side="left">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7 min-w-[74px] px-2.5 text-xs font-medium"
                disabled={isResolving || isComposing}
                aria-label={`Escalate ${fieldLabel} to human`}
                onClick={openComposer}
              >
                Escalate
              </Button>
            </SimpleTooltipV2>
            {!isCaseLevel && (
              <SimpleTooltipV2 content={resolveTooltip} side="left">
                <Button
                  type="button"
                  variant={isResolving ? 'secondary' : 'outline'}
                  size="sm"
                  className="h-7 min-w-[74px] px-2.5 text-xs font-medium"
                  disabled={isComposing}
                  aria-pressed={isResolving}
                  aria-label={`Resolve ${fieldLabel} by editing the field`}
                  onClick={() => setIsResolving(v => !v)}
                >
                  Resolve
                </Button>
              </SimpleTooltipV2>
            )}
          </div>
        )}
      </div>

      {/* The escalation note box, right under the field it belongs to. Save moves the
          action into the "Escalate to human caller" section below the list, which is where
          the staged note is then shown and edited — this row goes away with it. */}
      {canEdit && isComposing && (
        <div className="rounded-md border border-muted bg-muted/20 px-2 py-2">
          <Textarea
            value={draft ?? ''}
            onChange={e => setDraft(e.target.value)}
            placeholder="Reason for escalation..."
            className="min-h-[56px] text-sm"
            aria-label={`Escalation note for ${fieldLabel}`}
            autoFocus
          />
          <div className="mt-2 flex justify-end gap-1.5">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 px-2.5 text-xs font-medium"
              onClick={closeComposer}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 px-2.5 text-xs font-medium"
              disabled={(draft ?? '').trim() === ''}
              onClick={saveComposer}
            >
              Save
            </Button>
          </div>
        </div>
      )}
    </li>
  );
};

export const HandoffActionRow = React.memo(HandoffActionRowComponent);
HandoffActionRow.displayName = 'HandoffActionRow';
